import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ProgramacionMantenimientoService } from "services/ProgramacionMantenimientoService";
import { notifyError } from "jsx/components/CitoPlus/components/CitoPlusToastr";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import { useProgramacionesMantenimiento } from "./hooks/useProgramacionesMantenimiento";
import { useProveedoresMantenimiento } from "./hooks/useProveedoresMantenimiento";
import ProgramacionFormModal from "./components/ProgramacionFormModal";
import ProgramacionDetailsModal from "./components/ProgramacionDetailsModal";
import { CitoPlusLinkIconPrimary, CitoPlusLinkIconSecondary, CitoPlusLinkIconDanger } from "jsx/components/CitoPlus/components/CitoPlusLink";
import swal from "sweetalert";
import { ButtonGroup } from "react-bootstrap";

const ProgramacionesMantenimiento = () => {
  const { programaciones, isLoading, error, createProgramacion, updateProgramacion, deleteProgramacion } = useProgramacionesMantenimiento();
  const { proveedores, createProveedor } = useProveedoresMantenimiento();
  const [showModal, setShowModal] = useState(false);
  const [editingProgramacion, setEditingProgramacion] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedProgramacion, setSelectedProgramacion] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleOpenModal = useCallback(() => {
    setEditingProgramacion(null);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingProgramacion(null);
  }, []);

  const handleEdit = useCallback((prog) => {
    setEditingProgramacion(prog);
    setShowModal(true);
  }, []);

  const handleVerDetalles = useCallback((prog) => {
    setSelectedProgramacion(prog);
    setShowDetailsModal(true);
  }, []);

  const handleDelete = useCallback(async (prog) => {
    swal({
      title: "¿Estás seguro?",
      text: `¿Deseas eliminar la programación "${prog.titulo}"?`,
      icon: "warning",
      buttons: ["Cancelar", "Eliminar"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await deleteProgramacion(prog.uuid);
          swal("Eliminado", "La programación ha sido eliminada.", "success");
        } catch (err) {
          swal("Error", "No se pudo eliminar la programación.", "error");
        }
      }
    });
  }, [deleteProgramacion]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const response = await ProgramacionMantenimientoService.exportar();

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `programaciones-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notifyError("No fue posible exportar las programaciones.");
    } finally {
      setIsExporting(false);
    }
  }, []);

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      if (editingProgramacion) {
        await updateProgramacion(editingProgramacion.uuid, formData);
      } else {
        await createProgramacion(formData);
      }
      handleCloseModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateProveedor = async (proveedorData) => {
    return await createProveedor(proveedorData);
  };

  if (isLoading) {
    return (
      <div className="row">
        <div className="col-12 text-center py-5">
          <SpinnerCitoPlus text="Cargando programaciones..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-danger">
            Error al cargar las programaciones. Por favor intente nuevamente.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title">Programaciones de Mantenimiento</h4>
              <div>
                <button className="btn btn-primary btn-sm me-2" onClick={handleOpenModal}>
                  <i className="fas fa-plus me-2"></i>
                  Crear Programación
                </button>
                <button
                  className="btn btn-outline-primary btn-sm me-2"
                  onClick={handleExport}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <span className="d-inline-flex align-items-center gap-2">
                      <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />
                      <span>Exportando...</span>
                    </span>
                  ) : (
                    <>
                      <i className="fas fa-file-excel me-2"></i>
                      Exportar
                    </>
                  )}
                </button>
                <Link to="/activos" className="btn btn-secondary btn-sm">
                  <i className="bi bi-arrow-left me-2"></i>
                  Volver a Activos
                </Link>
              </div>
            </div>
            <div className="card-body">
              {programaciones.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-calendar-check" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                  <p className="mt-3 text-muted">No hay programaciones de mantenimiento registradas.</p>
                  <button className="btn btn-primary" onClick={handleOpenModal}>
                    <i className="bi bi-plus-lg me-2"></i>
                    Crear Programación
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Activo</th>
                        <th>Título</th>
                        <th>Tipo</th>
                        <th>Frecuencia</th>
                        <th>Fecha Inicio</th>
                        <th>Próxima Ejecución</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {programaciones.map((prog) => (
                        <tr key={prog.uuid}>
                          <td>{prog.activo?.nombre || "N/A"}</td>
                          <td>{prog.titulo}</td>
                          <td>
                            <span className={`badge bg-${prog.tipo === "preventivo" ? "success" : "warning"}`}>
                              {prog.tipo === "preventivo" ? "Preventivo" : "Correctivo"}
                            </span>
                          </td>
                          <td>{prog.frecuencia}</td>
                          <td>{prog.fecha_inicio}</td>
                          <td>{prog.fecha_siguiente}</td>
                          <td>
                            <span className={`badge bg-${prog.estado === "activo" ? "primary" : "secondary"}`}>
                              {prog.estado}
                            </span>
                          </td>
                          <td>
                            <ButtonGroup aria-label="Acciones">
                              <CitoPlusLinkIconSecondary
                                title="Ver"
                                onClick={() => handleVerDetalles(prog)}
                              >
                                <i className="fas fa-eye" />
                              </CitoPlusLinkIconSecondary>
                              <CitoPlusLinkIconPrimary
                                title="Editar"
                                onClick={() => handleEdit(prog)}
                              >
                                <i className="fas fa-edit" />
                              </CitoPlusLinkIconPrimary>
                              <CitoPlusLinkIconDanger
                                title="Eliminar"
                                onClick={() => handleDelete(prog)}
                              >
                                <i className="fas fa-trash-alt" />
                              </CitoPlusLinkIconDanger>
                            </ButtonGroup>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ProgramacionFormModal
        show={showModal}
        onHide={handleCloseModal}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        proveedores={proveedores}
        onCreateProveedor={handleCreateProveedor}
        initialData={editingProgramacion}
        isEditing={!!editingProgramacion}
      />

      <ProgramacionDetailsModal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          setSelectedProgramacion(null);
        }}
        uuid={selectedProgramacion?.uuid}
      />
    </>
  );
};

export default ProgramacionesMantenimiento;
